import { FieldValues } from 'react-hook-form'
import {
    GetOrders200Response,
    GetOrderUploadPresignedUrlRequest,
    OrderAppointmentsResponse,
    OrderDeliveriesResponse,
    OrderDetailResponse,
    OrderMessageThreadsResponse,
    OrderPhase,
    OrderUploadResponse,
    OrderUploadsListResponse,
    PostOrderRequestInvoiceDetails,
    PresignedUploadResponse,
    Product,
    ProductOption
} from '../../model'
import { del, get, post, put } from './api'

export interface ExtendedProductOption extends ProductOption {
    _isAdded?: boolean
}

export interface ExtendedProduct extends Product {
    _isPack?: boolean
    availableOptions?: ExtendedProductOption[]
    products?: ExtendedProduct[]
}

export const getOrders = async (query?: {
    searchString?: string
    phase?: OrderPhase | undefined
    page?: number
    pageSize?: number
}): Promise<GetOrders200Response> => {
    const { data } = await get('/orders', {
        params: {
            ...query
        }
    })

    return data
}

export const retrieveAddress = async (params: FieldValues) => {
    const { data } = await put('/orders/retrieve-address', {
        data: params
    })

    return data
}

export const calculateTotalOrderPrice = async (params: {
    zipcode: string
    houseNumber: number
    extension?: string
    category: number
    packs?: number[]
    products?: number[]
    productOptions?: ExtendedProductOption[]
}) => {
    const { data } = await put('/orders/calculate', {
        data: params
    })

    return data
}

export const createOrder = async (params: {
    category: number
    zipcode: string
    houseNumber: number
    extension: string
    invoiceDetails?: PostOrderRequestInvoiceDetails
    invoice3rdParty?: 'yes' | 'no'
    surface?: string
    packs?: number[]
    products?: number[]
    productOptions?: number[]
    remarks?: string
}) => {
    const { data } = await post('/orders', {
        data: params
    })

    return data
}

export const getMappedProductOptions = (data: ExtendedProduct[]) => {
    const opts: ExtendedProduct[] =
        data.length > 0
            ? data
                  .map((a: ExtendedProduct) => {
                      if (a._isPack) {
                          return a.products || []
                      } else {
                          return a
                      }
                  })
                  .flat()
            : []

    const allOptions =
        opts.length > 0 &&
        opts
            .filter((a) => a.availableOptions)
            .map((a: any) => a.availableOptions)
            .flat()
            .filter((a: any) => a._isAdded) // filter undefined values

    // get all product options that are added
    const productOptions = allOptions && allOptions.length > 0 && allOptions.map((a: any) => a.id)

    return productOptions || []
}

// ORDER DETAILS RESPONSE

export const getOrderDetails = async (orderId: number): Promise<OrderDetailResponse> => {
    const { data } = await get(`/orders/${orderId}`)
    return data
}

export const getOrderDetailMessageThreads = async (
    orderId: number,
    query?: { page?: number; pageSize?: number }
): Promise<OrderMessageThreadsResponse> => {
    const { data } = await get(`/orders/${orderId}/messageThreads`, {
        params: {
            ...query
        }
    })
    return data
}

export const getOrderDetailDeliveries = async (
    orderId: number,
    query?: { page?: number; pageSize?: number }
): Promise<OrderDeliveriesResponse> => {
    const { data } = await get(`/orders/${orderId}/deliveries`, {
        params: {
            ...query
        }
    })
    return data
}

export const getOrderDetailAppointments = async (
    orderId: number,
    query?: { page?: number; pageSize?: number }
): Promise<OrderAppointmentsResponse> => {
    const { data } = await get(`/orders/${orderId}/appointments`, {
        params: {
            ...query
        }
    })
    return data
}

export const getOrderDetailUploads = async (orderId: number): Promise<OrderUploadsListResponse> => {
    const { data } = await get(`/orders/${orderId}/uploads`)
    return data
}

export const postOrderDetailUploads = async (orderId: number, formData: FormData): Promise<OrderUploadResponse> => {
    const { data } = await post(`/orders/${orderId}/uploads`, {
        data: formData,
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    })
    return data
}

// Get presigned URL from the backend
export const getPresignedOrderDetailUploadFile = async (
    orderId: number,
    file: File
): Promise<GetOrderUploadPresignedUrlRequest> => {
    const results = (await post(`/orders/${orderId}/uploads/presigned-url`, {
        data: {
            fileName: file.name,
            fileSize: file.size,
            contentType: file.type
        }
    })) as unknown as GetOrderUploadPresignedUrlRequest

    return results
}

// Upload file to S3 using the presigned URL
export const uploadToS3 = async (uploadUrl: string, file: File) => {
    const results = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
            'Content-Type': file.type
        }
    })

    return results
}

// Confirm to the backend that the upload to S3 is complete
export const confirmUploadToS3 = async (orderId: number, s3Key: string, file: File) => {
    const results = await post(`/orders/${orderId}/uploads/complete`, {
        data: { s3Key, fileName: file.name }
    })
    return results
}

// Full flow: get presigned URL, upload to S3, confirm upload
export const getPresignedUrlandUploadToS3Directly = async (orderId: number, file: File) => {
    const data = (await getPresignedOrderDetailUploadFile(orderId, file)) as unknown as PresignedUploadResponse
    const results = await uploadToS3(data.uploadUrl, file)
    if (results.status !== 200) {
        return results
    }
    return await confirmUploadToS3(orderId, data.s3Key, file)
}

export const deleteOrderDetailUploadFile = async (orderId: number, fileId: string) => {
    const { data } = await del(`/orders/${orderId}/uploads/${fileId}`)
    return data
}

export const downloadOrderDetailUploadFile = async (orderId: number, fileId: string) => {
    const { data } = await get(`/orders/${orderId}/uploads/${fileId}/download`)
    return data
}
